import { ApiToken } from '@app/core/interfaces/user/api-token.interface';
import { addDays, addYears, differenceInCalendarDays } from 'date-fns';

/** Quantos dias antes do vencimento o token passa a aparecer como "Vence em N dias". */
export const API_TOKEN_EXPIRING_SOON_DAYS = 30;

/** Situação do token como a modal mostra: a etiqueta e a cor. */
export interface ApiTokenStatus {
  label: string;
  tone: 'success' | 'warn' | 'danger' | 'neutral';
  /** Revogado ou expirado: não dá mais para copiar nem revogar. */
  inactive: boolean;
}

/** Atalhos de validade do formulário de token novo; `custom` abre o calendário. */
export type ApiTokenValidityPreset = '30d' | '90d' | '1y' | '10y' | 'custom';

export const API_TOKEN_VALIDITY_PRESETS: { value: ApiTokenValidityPreset; label: string }[] = [
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: '1y', label: '1 ano' },
  { value: '10y', label: '10 anos' },
  { value: 'custom', label: 'Outra data' },
];

/**
 * Data de vencimento de um atalho de validade, contada a partir de hoje. `custom` devolve `null`
 * (a data vem do calendário). `today` vem do `DateFormatterService.getTodayDate()`.
 */
export function expirationFromPreset(preset: ApiTokenValidityPreset, today: Date): Date | null {
  switch (preset) {
    case '30d':
      return addDays(today, 30);
    case '90d':
      return addDays(today, 90);
    case '1y':
      return addYears(today, 1);
    case '10y':
      return addYears(today, 10);
    default:
      return null;
  }
}

/**
 * Situação do token para a etiqueta da lista: revogado, expirado, vencendo em até
 * {@link API_TOKEN_EXPIRING_SOON_DAYS} dias ou ativo. As datas já vêm parseadas pelo
 * `DateFormatterService` (o chamador passa `expiration` e `today`).
 */
export function apiTokenStatus(
  token: ApiToken,
  expiration: Date | null | undefined,
  today: Date
): ApiTokenStatus {
  if (token.revokedAt) {
    return { label: 'Revogado', tone: 'neutral', inactive: true };
  }

  if (expiration) {
    // O vencimento é gravado como data (meia-noite) e o backend recusa o token a partir dela:
    // no próprio dia ele já está vencido, então não há "Vence hoje".
    const daysLeft = differenceInCalendarDays(expiration, today);
    if (daysLeft <= 0) {
      return { label: 'Expirado', tone: 'danger', inactive: true };
    }
    if (daysLeft <= API_TOKEN_EXPIRING_SOON_DAYS) {
      return {
        label: `Vence em ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}`,
        tone: 'warn',
        inactive: false,
      };
    }
  }

  return { label: 'Ativo', tone: 'success', inactive: false };
}

/** Mensagem de negócio do backend (`errors[].message`), ou o texto de reserva para falha técnica. */
export function describeApiError(error: unknown, fallback: string): string {
  const errors = (error as { errors?: { message?: string }[] } | null)?.errors;
  const messages = (errors ?? []).map(item => item?.message).filter((m): m is string => !!m);
  return messages.length > 0 ? messages.join(' ') : fallback;
}
